/******************************************************************************
==============================================================================
BabiGO

NotificationDropdown

Affiche les notifications de l'utilisateur connecté.

==============================================================================*/

import {

    useMemo,

    useEffect,

    useRef,

} from "react";

import {

    Link,

} from "react-router-dom";

import {

    FiExternalLink,

    FiRefreshCw,

    FiCheck,

    FiX,

} from "react-icons/fi";

import useNotifications

    from "../../hooks/useNotifications";

import NotificationItem

    from "./NotificationItem";

import NotificationEmpty

    from "./NotificationEmpty";

import NotificationSkeleton

    from "./NotificationSkeleton";

import "./notificationDropdown.css";

export default function NotificationDropdown({

    isOpen,

    onClose,

    anchorRef = {},

}) {
    /* ======================================================================
       NOTIFICATIONS
    ====================================================================== */

    const {

        activeNotifications,

        unreadCount,

        totalNotifications,

        loading,

        isEmpty,

        refresh,

        markAllAsRead,
    
        markAsRead,
    
        markAsUnread,
    
        archive,
    
        deleteNotification,

    } = useNotifications();

    /* ======================================================================
    REGROUPEMENT DES NOTIFICATIONS
    ====================================================================== */

    const groupedNotifications = useMemo(() => {

        const groups = {

            today: [],

            yesterday: [],

            thisWeek: [],

            older: [],

        };

        const now = new Date();

        const yesterday = new Date();

        yesterday.setDate(

            yesterday.getDate() - 1

        );

        activeNotifications.forEach(

            (notification) => {

                const createdAt =

                    notification.createdAt?.toDate

                        ? notification.createdAt.toDate()

                        : new Date(

                            notification.createdAt

                        );

                if (

                    createdAt.toDateString() ===

                    now.toDateString()

                ) {

                    groups.today.push(

                        notification

                    );

                }

                else if (

                    createdAt.toDateString() ===

                    yesterday.toDateString()

                ) {

                    groups.yesterday.push(

                        notification

                    );

                }

                else {

                    const diffDays =

                        Math.floor(

                            (

                                now -

                                createdAt

                            ) /

                            (

                                1000 *

                                60 *

                                60 *

                                24

                            )

                        );

                    if (

                        diffDays <= 7

                    ) {

                        groups.thisWeek.push(

                            notification

                        );

                    }

                    else {

                        groups.older.push(

                            notification

                        );

                    }

                }

            }

        );

        return groups;

    }, [

        activeNotifications,

    ]);

    const dropdownRef = useRef(null);

    /* ======================================================================
    CLICK OUTSIDE
    ====================================================================== */

    useEffect(() => {

        if (!isOpen) {

            return;

        }

        function handleClickOutside(event) {

            if (

                dropdownRef.current &&

                !dropdownRef.current.contains(event.target)

            ) {

                onClose();

            }

        }

        document.addEventListener(

            "mousedown",

            handleClickOutside

        );

        return () => {

            document.removeEventListener(

                "mousedown",

                handleClickOutside

            );

        };

    }, [

        isOpen,

        onClose,

    ]);

    /* ======================================================================
    ESCAPE
    ====================================================================== */

    useEffect(() => {

        if (!isOpen) {

            return;

        }

        function handleEscape(event) {

            if (

                event.key === "Escape"

            ) {

                onClose();

            }

        }

        window.addEventListener(

            "keydown",

            handleEscape

        );

        return () => {

            window.removeEventListener(

                "keydown",

                handleEscape

            );

        };

    }, [

        isOpen,

        onClose,

    ]);

    /* ======================================================================
       UI
    ====================================================================== */

    return (

        <div

            ref={dropdownRef}

            className={

                isOpen

                    ? "notification-dropdown open"

                    : "notification-dropdown"

            }

        >

            <div className="notification-dropdown-header">

                <div>

                    <h3>

                        Notifications

                    </h3>

                    <span className="notification-counter">

                        {unreadCount}

                        {

                            unreadCount > 1

                                ? " non lues"

                                : " non lue"

                        }

                    </span>

                </div>

                <div className="notification-header-actions">

                    <button

                        type="button"

                        className="mark-all-button"

                        disabled={

                            unreadCount === 0

                        }

                        onClick={

                            markAllAsRead

                        }

                    >

                        <FiCheck />

                    </button>

                    <button

                        type="button"

                        className="close-dropdown-button"

                        onClick={

                            onClose

                        }

                    >

                        <FiX />

                    </button>

                </div>

            </div>

            {/* ==========================================================
                CONTENU
            ========================================================== */}

            <div className="notification-dropdown-body">

                {

                    loading && (

                        <NotificationSkeleton />

                    )

                }

                {

                    !loading && isEmpty && (

                        <NotificationEmpty
                            title="Aucune notification"
                            message="Les nouvelles notifications apparaîtront ici."
                        />

                    )

                }

                {

                    !loading &&

                    !isEmpty && (

                        <>

                            {

                                groupedNotifications.today.length > 0 && (

                                    <section className="notification-group">

                                        <h4 className="notification-group-title">

                                            Aujourd'hui

                                        </h4>

                                        {

                                            groupedNotifications.today.map(

                                                (notification) => (

                                                    <NotificationItem
                                                        notification={notification}
                                                        onClick={onClose}
                                                        onMarkAsRead={markAsRead}
                                                        onMarkAsUnread={markAsUnread}
                                                        onArchive={archive}
                                                        onDelete={deleteNotification}
                                                    />

                                                )

                                            )

                                        }

                                    </section>

                                )

                            }

                            {

                                groupedNotifications.yesterday.length > 0 && (

                                    <section className="notification-group">

                                        <h4 className="notification-group-title">

                                            Hier

                                        </h4>

                                        {

                                            groupedNotifications.yesterday.map(

                                                (notification) => (

                                                    <NotificationItem

                                                        key={notification.id}

                                                        notification={notification}
                                                        
                                                        onClick={onClose}

                                                    />

                                                )

                                            )

                                        }

                                    </section>

                                )

                            }

                            {

                                groupedNotifications.thisWeek.length > 0 && (

                                    <section className="notification-group">

                                        <h4 className="notification-group-title">

                                            Cette semaine

                                        </h4>

                                        {

                                            groupedNotifications.thisWeek.map(

                                                (notification) => (

                                                    <NotificationItem

                                                        key={notification.id}

                                                        notification={notification}
                                                        
                                                        onClick={onClose}

                                                    />

                                                )

                                            )

                                        }

                                    </section>

                                )

                            }

                            {

                                groupedNotifications.older.length > 0 && (

                                    <section className="notification-group">

                                        <h4 className="notification-group-title">

                                            Plus anciennes

                                        </h4>

                                        {

                                            groupedNotifications.older.map(

                                                (notification) => (

                                                    <NotificationItem

                                                        key={notification.id}

                                                        notification={notification}
                                                        
                                                        onClick={onClose}

                                                    />

                                                )

                                            )

                                        }

                                    </section>

                                )

                            }

                        </>

                    )

                }

            </div>

            {/* ==========================================================
                FOOTER
            ========================================================== */}

            <div className="notification-dropdown-footer">

                <div className="notification-footer-left">

                    <span className="notification-total">

                        {totalNotifications}

                        {

                            totalNotifications > 1

                                ? " notifications"

                                : " notification"

                        }

                    </span>

                </div>

                <div className="notification-footer-actions">

                    <button

                        type="button"

                        className="notification-refresh-button"

                        onClick={refresh}

                        title="Actualiser"

                        disabled={loading}

                    >

                        <FiRefreshCw
                            className={loading ? "rotating" : ""}
                        />

                    </button>

                    <Link

                        to="/notifications"

                        className="notification-all-link"

                        onClick={onClose}

                    >

                        <span>

                            Voir tout

                        </span>

                        <FiExternalLink />

                    </Link>

                </div>

            </div>
        
        </div>
    )
}


